import { useEffect, useMemo, useState } from 'react';
import { ParrotChat } from '@parrot/parrot-chat';
import { getTheme, type Theme } from '../../lib/theme';
import styles from './plexie-drawer.module.css';

/**
 * Plexie Assistant — the Streaming Economics "Ask PLEXIE" chat drawer.
 *
 * Thin wrapper around <ParrotChat> from @parrot/parrot-chat, wired to the shared
 * parrot-labs intelligence streaming endpoint
 * (…/parrot-labs/intelligence/agents/chat/stream — baked into the package).
 * The drawer chrome (slide-in panel, backdrop, fullscreen) lives here; the chat
 * surface, composer and conversation history are owned by the package.
 *
 * Adapted from apps/intelligence-platform's reference drawer to this app's
 * Vite + React Router conventions — no Next `'use client'`, no AuthContext /
 * ThemeContext. Auth and theme come from the app's own localStorage-backed
 * sources instead.
 */

export interface PlexieDrawerProps {
  open: boolean;
  onClose: () => void;
}

/** Registered agent_type on the shared parrot-labs intelligence endpoint. */
const PLEXIE_AGENT_TYPE = 'streaming-economics';

const PLEXIE_CDN = 'https://cdn-fastly.parrotanalytics.com/general-assets';

export function PlexieDrawer({ open, onClose }: PlexieDrawerProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Theme: the app persists its light/dark preference under localStorage 'theme'
  // (see lib/theme.ts). Mirror it here and keep it in sync with cross-tab/app
  // toggles via the same 'storage' event ThemeToggle listens to. Falls back to
  // "dark" when there's no window (defensive; this is a Vite SPA).
  const [theme, setThemeState] = useState<Theme>(() =>
    typeof window !== 'undefined' ? getTheme() : 'dark',
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setThemeState(getTheme());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Email scopes the package's server-backed conversation history store. SSO
  // stores it under 'user_email' (see pages/sso/SsoCallback.tsx).
  const [email, setEmail] = useState<string>();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const e = localStorage.getItem('user_email');
    if (e) setEmail(e);
  }, []);

  // Platform auth token — resolved lazily on first open, exactly as the rest of
  // the app does (SSO stores the platform session token under 'auth_token').
  // ParrotChat sends it as the `x-auth-token` header; the stream endpoint
  // rejects requests without it.
  const [authToken, setAuthToken] = useState<string>();
  useEffect(() => {
    if (!open || authToken || typeof window === 'undefined') return;
    const t = localStorage.getItem('auth_token');
    if (t) setAuthToken(t);
  }, [open, authToken]);

  const welcome = useMemo(
    () => ({
      title: "Hi, I'm PLEXIE",
      subtitle: 'Your Streaming Economics Analyst',
      iconUrl: `${PLEXIE_CDN}/plexie-icon-v3.png`,
    }),
    [],
  );

  return (
    <>
      {/* Backdrop — hidden in fullscreen */}
      {!isFullScreen && (
        <div
          className={styles.backdrop}
          data-open={open}
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer panel — slides in from the right */}
      <aside
        className={styles.drawer}
        data-open={open}
        data-fullscreen={isFullScreen}
        aria-label="Plexie Assistant"
      >
        <ParrotChat
          agentType={PLEXIE_AGENT_TYPE}
          responseMode="stream"
          email={email}
          authToken={authToken}
          theme={theme}
          welcome={welcome}
          suggestions={[
            {
              label: 'Platform Metrics',
              questions: [
                'Compare ARPU across Netflix and Disney+',
                'Which platform grew subscribers fastest last quarter?',
                'Show revenue vs audience for Max',
                "What's the subscriber trend for Prime Video?",
              ],
            },
            {
              label: 'Title Valuation',
              questions: [
                'Value the top titles on Netflix this quarter',
                'Which Disney+ originals drive the most demand?',
                'Rank Max titles by audience impact',
                "What's the economic value of Prime Video's top drama?",
              ],
            },
          ]}
          assistantIconUrl={`${PLEXIE_CDN}/plexie-icon-v3.png`}
          placeholder="Ask PLEXIE about streaming platforms, subscribers, revenue and title valuation"
          onClose={onClose}
          /* Sidebar — conversation history (package default server-backed store) */
          sidebarOpen={sidebarOpen}
          onSidebarToggle={() => setSidebarOpen((o) => !o)}
          sidebar={{ collapseMode: 'rail' }}
          /* Fullscreen toggle */
          isFullScreen={isFullScreen}
          onFullScreenClick={() => setIsFullScreen((f) => !f)}
        />
      </aside>
    </>
  );
}
